/**
 * NotificationBell — the header bell + dropdown notification panel.
 *
 * Aggregates the worker's active alerts (profile incomplete, plus any
 * context-specific ones passed in via `extra`) into a list. A red dot shows
 * on the bell while there are unread items; tapping opens a panel. Replaces
 * the old yellow "profile incomplete" banner and is the home for future
 * notifications.
 */

import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { Bell, UserCircle, Clock, ShieldWarning } from '@phosphor-icons/react';

const REQUIRED_FIELDS = [
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'birthday', label: 'Birthday' },
  { key: 'abn', label: 'ABN' },
  { key: 'bsb', label: 'BSB' },
  { key: 'account_number', label: 'Account Number' },
];

// Icon per notification kind.
const KIND_ICON = {
  profile:   <UserCircle size={18} weight="duotone" color="#b45309" />,
  clockin:   <Clock size={18} weight="duotone" color="#2563eb" />,
  insurance: <ShieldWarning size={18} weight="duotone" color="#dc2626" />,
};

export default function NotificationBell({ onAccount, extra = [] }) {
  const [missing, setMissing] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.getProfile()
      .then(p => setMissing(REQUIRED_FIELDS.filter(f => !p[f.key] || p[f.key].toString().trim() === '')))
      .catch(() => {});
  }, []);

  const notes = [];
  if (missing.length) {
    notes.push({
      id: 'profile', kind: 'profile',
      title: 'Complete your profile',
      body: `Missing: ${missing.map(f => f.label).join(', ')}. Add these so you get paid on time.`,
      actionLabel: 'Complete now',
      onAction: onAccount,
    });
  }
  notes.push(...extra);

  const hasUnread = notes.length > 0;

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        title="Notifications"
        aria-label={`Notifications${hasUnread ? ` (${notes.length})` : ''}`}
        style={{ position: 'relative', background: '#f3f4f6', border: 'none', width: 38, height: 38, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1e3a4a', cursor: 'pointer' }}
      >
        <Bell size={19} weight={hasUnread ? 'fill' : 'regular'} />
        {hasUnread && (
          <span style={{ position: 'absolute', top: 7, right: 8, minWidth: 8, height: 8, background: '#dc2626', borderRadius: 999, border: '1.5px solid #f3f4f6' }} />
        )}
      </button>

      {open && (
        <>
          {/* outside-tap catcher */}
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 199 }} />
          <div style={{
            position: 'absolute', top: 46, right: 0, width: 288, maxWidth: '86vw',
            background: '#fff', borderRadius: 14, boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
            border: '1px solid #eef0f2', zIndex: 200, overflow: 'hidden',
          }}>
            <div style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 800, color: '#0F2233' }}>
              Notifications
            </div>
            {notes.length === 0 ? (
              <div style={{ padding: '22px 14px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                🎉 You're all caught up.
              </div>
            ) : (
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                {notes.map(n => (
                  <div key={n.id} style={{ display: 'flex', gap: 10, padding: '12px 14px', borderBottom: '1px solid #f8fafc' }}>
                    <div style={{ flexShrink: 0, marginTop: 1 }}>{KIND_ICON[n.kind] || <Bell size={18} weight="duotone" color="#64748b" />}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>{n.title}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, lineHeight: 1.4 }}>{n.body}</div>
                      {n.onAction && (
                        <button
                          onClick={() => { setOpen(false); n.onAction(); }}
                          style={{ marginTop: 8, background: '#1e3a4a', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                        >
                          {n.actionLabel || 'Open'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
